import type { components } from './generated-types';
import type { UnitType, DocumentType } from './generated-types';
import type { AnalyticsFilters, PackagingExtractedData } from './types';

export type JobFilters = components['schemas']['JobFilterRequest'];

export const mutationKeys = {
  substances: {
    verify: ['substances', 'verify'] as const,
  },
  jobs: {
    cancel: ['jobs', 'cancel'] as const,
  },
  documents: {
    rerun: ['documents', 'rerun'] as const,
  },
  transactions: {
    errorCheck: (id: string) => ['transactions', 'error-check', id] as const,
    loadErrorCheck: (id: string) => ['transactions', 'error-check', id, 'load'] as const,
    curatedWrite: (id: string) => ['transactions', 'curated-write', id] as const,
  },
};

export const queryKeys = {
  documents: {
    all: ['documents'] as const,
    history: () => [...queryKeys.documents.all, 'history'] as const,
  },

  jobs: {
    all: ['jobs'] as const,
    list: (filters: JobFilters) => [...queryKeys.jobs.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.jobs.all, 'detail', id] as const,
  },

  extractionModels: {
    all: ['extraction-models'] as const,
    list: () => [...queryKeys.extractionModels.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.extractionModels.all, 'detail', id] as const,
  },

  curation: {
    all: ['curation'] as const,
  },

  transactions: {
    all: ['transactions'] as const,
    full: (id: string) => [...queryKeys.transactions.all, 'full', id] as const,
    artifact: (id: string, name: string) =>
      [...queryKeys.transactions.all, 'artifact', id, name] as const,
    pdf: (id: string) => [...queryKeys.transactions.all, 'pdf', id] as const,
    errorReview: (id: string) => [...queryKeys.transactions.all, 'error-review', id] as const,
    exportPreview: (id: string) => [...queryKeys.transactions.all, 'export-preview', id] as const,
    pageImage: (imageUrl: string) =>
      [...queryKeys.transactions.all, 'pageImage', imageUrl] as const,
  },

  credits: {
    all: ['credits'] as const,
    balance: () => [...queryKeys.credits.all, 'balance'] as const,
    history: (page: number, pageSize: number) =>
      [...queryKeys.credits.all, 'history', page, pageSize] as const,
  },

  substances: {
    all: ['substances'] as const,
    hierarchy: (
      substanceId: string,
      unitType: UnitType,
      isLq?: boolean,
      documentType?: DocumentType,
      extractedOuter?: PackagingExtractedData | null,
      extractedInner?: PackagingExtractedData | null
    ) =>
      [
        ...queryKeys.substances.all,
        'hierarchy',
        substanceId,
        unitType,
        isLq,
        documentType,
        extractedOuter ?? null,
        extractedInner ?? null,
      ] as const,
    rank: (
      substanceIds: string[],
      properShippingName?: string,
      hazardClass?: string,
      packingGroup?: string,
      subsidiaryHazards?: string[]
    ) =>
      [
        ...queryKeys.substances.all,
        'rank',
        [...substanceIds].sort(),
        properShippingName,
        hazardClass,
        packingGroup,
        subsidiaryHazards ? [...subsidiaryHazards].sort() : undefined,
      ] as const,
  },

  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
    teamMembers: (teamId?: string) => [...queryKeys.user.all, 'teamMembers', teamId] as const,
    teams: () => [...queryKeys.user.all, 'teams'] as const,
  },

  teams: {
    all: ['teams'] as const,
    settings: (teamId: string) => [...queryKeys.teams.all, 'settings', teamId] as const,
    modelAssignments: () => [...queryKeys.teams.all, 'model-assignments'] as const,
    deliveries: {
      all: (teamId: string) => [...queryKeys.teams.all, 'deliveries', teamId] as const,
      list: (
        teamId: string,
        page: number,
        pageSize: number,
        status?: string,
        event?: string,
        transactionId?: string
      ) =>
        [
          ...queryKeys.teams.all,
          'deliveries',
          teamId,
          'list',
          { page, pageSize, status, event, transactionId },
        ] as const,
      detail: (teamId: string, deliveryId: string) =>
        [...queryKeys.teams.all, 'deliveries', teamId, 'detail', deliveryId] as const,
    },
  },

  health: {
    all: ['health'] as const,
    validation: () => [...queryKeys.health.all, 'validation'] as const,
  },

  validation: {
    all: ['validation'] as const,
    request: (transactionId: string) =>
      [...queryKeys.validation.all, 'request', transactionId] as const,
    summary: (validationId: string) =>
      [...queryKeys.validation.all, 'summary', validationId] as const,
    sources: (validationId: string) =>
      [...queryKeys.validation.all, 'sources', validationId] as const,
  },

  ctuIsoCodes: {
    all: ['ctuIsoCodes'] as const,
    list: () => [...queryKeys.ctuIsoCodes.all, 'list'] as const,
  },

  analytics: {
    all: ['analytics'] as const,
    usage: (filters: AnalyticsFilters) => [...queryKeys.analytics.all, 'usage', filters] as const,
    curation: (filters: AnalyticsFilters) =>
      [...queryKeys.analytics.all, 'curation', filters] as const,
    extractionOutput: (filters: AnalyticsFilters) =>
      [...queryKeys.analytics.all, 'extraction-output', filters] as const,
    substanceValidation: (filters: AnalyticsFilters) =>
      [...queryKeys.analytics.all, 'substance-validation', filters] as const,
    extractionHealth: (filters: AnalyticsFilters) =>
      [...queryKeys.analytics.all, 'extraction-health', filters] as const,
    curationFieldChanges: (fieldKey: string, filters: AnalyticsFilters, page: number) =>
      [...queryKeys.analytics.all, 'curation-field-changes', fieldKey, filters, page] as const,
    curationFieldValueChanges: (
      fieldKey: string,
      filters: AnalyticsFilters,
      changeKind: string | undefined
    ) =>
      [
        ...queryKeys.analytics.all,
        'curation-field-value-changes',
        fieldKey,
        filters,
        changeKind ?? null,
      ] as const,
    extractionEvents: (
      filters: AnalyticsFilters,
      eventType: string | null,
      messageFilter: string | null,
      page: number
    ) =>
      [
        ...queryKeys.analytics.all,
        'extraction-events',
        eventType,
        messageFilter,
        filters,
        page,
      ] as const,
  },

  extraction: {
    all: ['extraction'] as const,
    result: (transactionId: string) =>
      [...queryKeys.extraction.all, 'result', transactionId] as const,
  },

  accounts: {
    all: ['accounts'] as const,
    teamMembers: (page: number, size: number, filter?: string) =>
      [...queryKeys.accounts.all, 'teamMembers', page, size, filter] as const,
  },

  regulations: {
    all: ['regulations'] as const,
    choices: (validationId: string | undefined, documentType: DocumentType) =>
      [...queryKeys.regulations.all, 'choices', validationId ?? null, documentType] as const,
    forTransaction: (transactionId: string) =>
      [...queryKeys.regulations.all, 'transaction', transactionId] as const,
  },

  openapi: {
    all: ['openapi'] as const,
    spec: (version: string) => [...queryKeys.openapi.all, 'spec', version] as const,
  },
};
